import * as React from "react"
import { useRouteMatch } from "react-router-dom"
import { Interval } from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import {
  type MetricSeries,
  type MetricValue,
} from "src/frontend/components/Shared/Layout/Components/Charts/toMetricSeries"
import { type ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import { type ComponentSizeProps } from "src/frontend/components/Shared/Layout/types"
import { useIsEditLayoutActive } from "src/frontend/components/Shared/Portals/State/useIsEditLayoutActive"
import routes from "src/frontend/routes"
/*
  STYLES
*/

const Summary = styled.div<ComponentSizeProps & { isEditLayoutActive: boolean }>`
  display: grid;
  grid-template-areas:
    "header    menu"
    "subheader none";
  grid-template-columns: 1fr auto;
  margin-bottom: ${({ componentSize }) => (componentSize.isExtraLarge ? "26px" : "16px")};
  margin-right: ${({ isEditLayoutActive }) => (isEditLayoutActive ? "10px" : "0")};
`

const Header = styled.header`
  grid-area: header;
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  height: 28px;
  gap: 6px;
  align-items: center;
`

const ComponentTitle = styled.div<ComponentSizeProps>`
  position: relative;
  flex: 1;

  font-size: ${({ componentSize }) => componentSize.headerSize};
  font-weight: ${fonts.weight.heavy};
  margin-bottom: 2px;

  display: flex;
  gap: 6px;
  align-items: center;
  align-self: center;

  color: ${colors.secondary};
  @media print {
    & {
      color: ${colors.black};
    }
  }
`

const TitleWrap = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  // since overflow is hidden the focus-visible outline on the child
  // is clipped, so propagate the outline to the title wrap instead
  &:has(> *:only-child:focus-visible) {
    outline: var(--outline);
  }
`

const Date = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const SubHeader = styled.div<ComponentSizeProps>`
  grid-area: subheader;

  color: ${colors.secondary50};
  @media print {
    & {
      color: ${colors.regentGray};
    }
  }

  font-size: ${({ componentSize }) => componentSize.subHeaderSize};
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px;
`

const HeaderValue = styled.div<ComponentSizeProps>`
  color: ${colors.secondary};
  @media print {
    & {
      color: ${colors.black};
    }
  }

  display: flex;
  flex-direction: row;
  align-items: baseline;
  margin-left: auto;
  gap: 4px;
  font-weight: ${fonts.weight.heavy};
  font-size: ${({ componentSize }) => componentSize.headerSize};
  margin-bottom: 2px;
`

const ValueMenu = styled.div`
  grid-area: menu;
  margin-left: 6px;
  align-self: center;
`

/*
  INTERFACES
*/

interface MetricComponentSummaryProps {
  className?: string
  componentSize: ComponentSize
  title: React.ReactNode | undefined
  icon?: React.ReactNode
  metricSeries: MetricSeries | undefined
  interval?: Interval
  defaultMetricSeriesIndex?: number
  selectedIndex?: number
  noDelta?: boolean
  valueMenu?: React.ReactNode
}

interface MetricComponentDateProps {
  metricSeriesToDisplay: MetricValue | undefined
  dateCustomNode?: React.ReactNode
  interval: Interval
}

interface MetricComponentTitleProps {
  className?: string
  componentSize: ComponentSize
  title: React.ReactNode | undefined
  icon?: React.ReactNode
  periodName: string | undefined
}

/*
  COMPONENTS
*/

export const MetricComponentSummary: React.FC<MetricComponentSummaryProps> = ({
  className,
  componentSize,
  title,
  icon,
  metricSeries,
  interval,
  defaultMetricSeriesIndex,
  selectedIndex,
  noDelta = false,
  valueMenu,
}) => {
  const isEditLayoutActive = useIsEditLayoutActive()

  const metricSeriesToDisplay =
    metricSeries &&
    metricSeries.values[selectedIndex ?? defaultMetricSeriesIndex ?? metricSeries.values.length - 1]
  const noActivity = React.useMemo(
    () => metricSeries?.values.every((value) => value.value === 0),
    [metricSeries]
  )

  return (
    <Summary
      className={className}
      componentSize={componentSize}
      isEditLayoutActive={isEditLayoutActive}
    >
      <Header>
        <ComponentTitle componentSize={componentSize}>
          {icon}
          <TitleWrap>{title}</TitleWrap>
        </ComponentTitle>
        <HeaderValue componentSize={componentSize}>
          {metricSeriesToDisplay && !noActivity && metricSeriesToDisplay.value}
        </HeaderValue>
      </Header>
      {metricSeriesToDisplay && (
        <SubHeader componentSize={componentSize}>
          <MetricComponentDate
            metricSeriesToDisplay={metricSeriesToDisplay}
            interval={interval || Interval.Month}
          />
        </SubHeader>
      )}
      {valueMenu && <ValueMenu>{valueMenu}</ValueMenu>}
    </Summary>
  )
}

export const MetricComponentDateSummary: React.FC<MetricComponentSummaryProps> = ({
  className,
  componentSize,
  title,
  icon,
  metricSeries,
  defaultMetricSeriesIndex,
  interval,
  selectedIndex,
  valueMenu,
}) => {
  // TODO: REFACTOR THIS global singletons usage!!
  const isEditLayoutActive = useIsEditLayoutActive()

  const metricSeriesToDisplay =
    metricSeries &&
    metricSeries.values[selectedIndex ?? defaultMetricSeriesIndex ?? metricSeries.values.length - 1]

  const subtitle = (
    <MetricComponentDate
      metricSeriesToDisplay={metricSeriesToDisplay}
      interval={interval || Interval.Month}
    />
  )

  return (
    <Summary
      className={className}
      componentSize={componentSize}
      isEditLayoutActive={isEditLayoutActive}
    >
      <Header>
        <ComponentTitle componentSize={componentSize}>
          {icon}
          <TitleWrap>{title}</TitleWrap>
        </ComponentTitle>
      </Header>
      <SubHeader componentSize={componentSize}>{subtitle}</SubHeader>
      {valueMenu && <ValueMenu>{valueMenu}</ValueMenu>}
    </Summary>
  )
}

export const MetricComponentHeaderTitle: React.FCWithRef<
  HTMLDivElement,
  MetricComponentTitleProps
> = ({ ref, className, componentSize, title, periodName, icon }) => (
  <Summary
    className={className}
    componentSize={componentSize}
    isEditLayoutActive={useIsEditLayoutActive()}
  >
    <Header>
      <ComponentTitle componentSize={componentSize}>
        {icon}
        <TitleWrap ref={ref}>{title}</TitleWrap>
      </ComponentTitle>
    </Header>
    <SubHeader componentSize={componentSize}>
      <Date>{periodName}</Date>
    </SubHeader>
  </Summary>
)

const MetricComponentDate: React.FC<MetricComponentDateProps> = ({
  metricSeriesToDisplay,
  interval,
}) => {
  const isPortalHome = useRouteMatch(routes.legalEntityHome.parameterizedPath)?.isExact

  if (!metricSeriesToDisplay) return null
  const { timestamp } = metricSeriesToDisplay

  if (
    isPortalHome &&
    dateTimeHelper.arePeriodsEqual(
      dateTimeHelper.periodFromDayjs(dateTimeHelper.dayjsFromGQLDate(timestamp).utc(), interval),
      dateTimeHelper.todayPeriodForInterval(interval)
    )
  ) {
    return <Date>as of Today</Date>
  }

  return <Date>{dateTimeHelper.displayNameFromDate(timestamp, interval)}</Date>
}
